import {
  useMutationSessionPackSubscriptionActivate,
  useMutationSessionPackSubscriptionExpiryDate,
} from "@gymflow/api";
import { LUXON_DATE_FORMAT } from "@gymflow/helpers";
import { getDateTimeInGym } from "apps/portal/src/helpers/date";
import { useClubSettings } from "apps/portal/src/providers";
import { ToastContext } from "apps/portal/src/providers/ToastProvider/context";
import useGymflowModels from "apps/portal/src/store";
import { isAxiosError } from "axios";
import { DateTime } from "luxon";
import { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { DatePicker } from "../../../molecules";
import { ConfirmModal } from "../../../templates";
import { Power01Icon } from "../../icons/Power01Icon";

type EditExpiryDateModalProps = {
  hide: () => void;
  initialDate?: string;
  isActivate?: boolean;
  subscriptionId: number;
  userMemberId: string;
};

export const EditExpiryDateModal = ({
  hide,
  initialDate,
  isActivate = false,
  subscriptionId,
  userMemberId,
}: EditExpiryDateModalProps) => {
  const { t } = useTranslation();
  const { timezone } = useClubSettings();
  const { api } = useGymflowModels();
  const { notifyDanger, toast } = useContext(ToastContext);

  const [expiryDate, setExpiryDate] = useState<string>(
    initialDate
      ? DateTime.fromISO(initialDate)
          .setZone(timezone)
          .startOf("day")
          .toFormat(LUXON_DATE_FORMAT)
      : DateTime.now()
          .setZone(timezone)
          .startOf("day")
          .toFormat(LUXON_DATE_FORMAT),
  );

  const { mutateAsync: mutateAsyncSessionPackSubscriptionActivate } =
    useMutationSessionPackSubscriptionActivate({ api });
  const { mutateAsync: mutateAsyncSessionPackSubscriptionExpiryDate } =
    useMutationSessionPackSubscriptionExpiryDate({ api });

  const onConfirm = useCallback(async () => {
    try {
      if (!expiryDate) {
        return;
      }
      const body = {
        subscriptionId,
        userMemberId,
        expiryDate,
      };
      if (isActivate) {
        await mutateAsyncSessionPackSubscriptionActivate(body);
      } else {
        await mutateAsyncSessionPackSubscriptionExpiryDate(body);
      }
      toast({
        message: t(
          "components.creditPackItem.toasts.creditPackSuccessfullyUpdated",
        ),
      });
      hide();
    } catch (e) {
      if (isAxiosError(e)) {
        notifyDanger(e);
      }
    }
  }, [
    expiryDate,
    hide,
    isActivate,
    mutateAsyncSessionPackSubscriptionActivate,
    mutateAsyncSessionPackSubscriptionExpiryDate,
    notifyDanger,
    subscriptionId,
    t,
    toast,
    userMemberId,
  ]);

  const disabledDate = useMemo(() => {
    const nowInGym = getDateTimeInGym(timezone);

    return {
      before: nowInGym.toDate(),
    };
  }, [timezone]);

  return (
    <ConfirmModal
      title={
        isActivate
          ? t(
              "components.creditPackItem.modal.editExpiryDateModal.title.activateCreditPack",
            )
          : t(
              "components.creditPackItem.modal.editExpiryDateModal.title.editExpiryDate",
            )
      }
      icon={
        isActivate ? (
          <Power01Icon
            className="size-5"
            pathClassName="stroke-secondary-700"
          />
        ) : undefined
      }
      onCancel={() => hide()}
      onConfirm={async () => await onConfirm()}
    >
      {t("components.creditPackItem.modal.editExpiryDateModal.description")}
      <div className="mt-4">
        <DatePicker
          inputClassnames="w-full"
          label={DateTime.fromFormat(expiryDate, LUXON_DATE_FORMAT)
            .setZone("local", { keepLocalTime: true })
            .toLocaleString(DateTime.DATE_MED)}
          mode="single"
          selected={DateTime.fromFormat(expiryDate, LUXON_DATE_FORMAT)
            .setZone("local", { keepLocalTime: true })
            .toJSDate()}
          disabled={disabledDate}
          handleDateSave={(value) =>
            setExpiryDate(
              DateTime.fromJSDate(value).toFormat(LUXON_DATE_FORMAT),
            )
          }
        />
      </div>
    </ConfirmModal>
  );
};
